import PropTypes from "prop-types";
import Chart from "react-apexcharts";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { useICCCColors, STATUS_FOR_PIE } from "./theme";
import { useMaterialUIController } from "context";

export default function OverallHealthPie({ rollup }) {
  const ICCC_COLORS = useICCCColors();
  const [controller] = useMaterialUIController();
  const total = rollup?.total || 0;
  const slices = STATUS_FOR_PIE.map((p) => ({
    ...p,
    value: rollup?.[p.key] || 0,
  }));

  const drawn = slices.filter((s) => s.value > 0);
  const series = drawn.map((s) => s.value);
  const labels = drawn.map((s) => s.label);
  const colors = drawn.map((s) => s.color);

  const options = {
    chart: { type: "donut", toolbar: { show: false }, foreColor: ICCC_COLORS.textSecondary },
    colors,
    labels,
    legend: { show: false },
    stroke: { width: 2, colors: [ICCC_COLORS.cardBg] },
    dataLabels: {
      enabled: true,
      formatter: (val) => `${Number(val).toFixed(1)}%`,
      style: { fontSize: "11px", fontWeight: 700, colors: ["#fff"] },
      dropShadow: { enabled: true, top: 1, left: 1, blur: 1, color: "#000", opacity: 0.45 },
    },
    plotOptions: { pie: { donut: { size: "45%" }, expandOnClick: false } },
    tooltip: {
      theme: controller?.darkMode ? "dark" : "light",
      y: { formatter: (val) => `${val.toLocaleString("en-IN")} devices` },
    },
  };

  return (
    <MDBox
      sx={{
        bgcolor: ICCC_COLORS.cardBg,
        border: `1px solid ${ICCC_COLORS.cardBorder}`,
        borderRadius: 1.5,
        p: 2, height: "100%",
        boxShadow: ICCC_COLORS.shadow,
      }}
    >
      <MDTypography variant="h6" fontWeight="bold" sx={{ mb: 1, color: ICCC_COLORS.textPrimary }}>
        Overall Device Health
      </MDTypography>
      <MDBox display="flex" gap={2} alignItems="center" sx={{ minHeight: 240 }}>
        <MDBox sx={{ flex: "0 0 50%", minWidth: 0 }}>
          {total > 0 && drawn.length > 0 ? (
            <Chart options={options} series={series} type="donut" height={240} />
          ) : (
            <MDBox sx={{ height: 240, display: "flex", alignItems: "center", justifyContent: "center", color: ICCC_COLORS.textMuted }}>
              No data
            </MDBox>
          )}
        </MDBox>
        <MDBox sx={{ flex: 1, minWidth: 0 }}>
          {slices.map((s) => {
            const pct = total ? (s.value / total) * 100 : 0;
            return (
              <MDBox key={s.key} display="flex" alignItems="center" mb={0.5}>
                <MDBox sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: s.color, mr: 1, flexShrink: 0 }} />
                <MDTypography variant="caption" sx={{ color: ICCC_COLORS.textSecondary, flex: 1, fontSize: 11 }}>
                  {s.label}
                </MDTypography>
                <MDTypography variant="caption" fontWeight="bold" sx={{ color: ICCC_COLORS.textPrimary, fontSize: 11 }}>
                  {s.value.toLocaleString("en-IN")}{" "}
                  <MDTypography component="span" variant="caption" sx={{ color: ICCC_COLORS.textMuted, fontWeight: 400, fontSize: 10 }}>
                    ({pct.toFixed(1)}%)
                  </MDTypography>
                </MDTypography>
              </MDBox>
            );
          })}
          <MDBox sx={{ borderTop: `1px dashed ${ICCC_COLORS.divider}`, mt: 1, pt: 0.75, display: "flex", justifyContent: "space-between" }}>
            <MDTypography variant="caption" sx={{ color: ICCC_COLORS.textSecondary, fontWeight: 600, fontSize: 11 }}>
              Total
            </MDTypography>
            <MDTypography variant="caption" fontWeight="bold" sx={{ color: ICCC_COLORS.textPrimary, fontSize: 11 }}>
              {total.toLocaleString("en-IN")} (100%)
            </MDTypography>
          </MDBox>
        </MDBox>
      </MDBox>
    </MDBox>
  );
}

OverallHealthPie.propTypes = {
  rollup: PropTypes.shape({
    total: PropTypes.number,
    healthy: PropTypes.number,
    warning: PropTypes.number,
    critical: PropTypes.number,
    unknown: PropTypes.number,
  }),
};
